import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Search, Mail, Phone, ToggleLeft, ToggleRight, ChevronRight, Trash2, UserX, UserCheck, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { DataTable, type ColumnDef } from '../../components/ui/DataTable';
import { Avatar } from '../../components/ui/Avatar';
import { PageHeader } from '../../components/ui/PageHeader';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPatch, apiDelete } from '../../api/client';
import type { Collaborator, BulkResult, ProjectCollaboratorAssignment } from '../../api/types';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Drawer } from '../../components/ui/Drawer';
import { Modal, ConfirmDialog } from '../../components/ui/Modal';
import { Input } from '../../components/ui/Input';
import { EmptyState } from '../../components/ui/EmptyState';
import { SkeletonRow } from '../../components/ui/Skeleton';
import { BulkActionBar } from '../../components/ui/BulkActionBar';
import { RowActionsMenu } from '../../components/ui/RowActionsMenu';
import { useTableSelection } from '../../hooks/useTableSelection';
import { COLLAB_ROLE_LABELS } from '../Projects/tabs/shared';

type CollabSortField = 'name' | 'role' | 'status';
type SortOrder = 'asc' | 'desc';

function sortCollaborators(list: import('../../api/types').Collaborator[], field: CollabSortField, order: SortOrder) {
  return [...list].sort((a, b) => {
    let cmp = 0;
    switch (field) {
      case 'name':   cmp = a.name.localeCompare(b.name); break;
      case 'role':   cmp = (a.role ?? '').localeCompare(b.role ?? ''); break;
      case 'status': cmp = Number(a.active) - Number(b.active); break;
    }
    return order === 'asc' ? cmp : -cmp;
  });
}

export default function CollaboratorsPage() {
  const qc = useQueryClient();
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [selected, setSelected] = useState<Collaborator | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState<Partial<Collaborator>>({});

  // Delete/action confirms
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [showBulkDeactivateConfirm, setShowBulkDeactivateConfirm] = useState(false);
  const [showBulkReactivateConfirm, setShowBulkReactivateConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);

  const [sortBy, setSortBy] = useState<CollabSortField>('name');
  const [sortOrder, setSortOrder] = useState<SortOrder>('asc');

  const resetKey = `${search}|${showInactive}`;
  const sel = useTableSelection(resetKey);

  const handleSort = (field: CollabSortField) => {
    if (sortBy === field) setSortOrder(o => o === 'asc' ? 'desc' : 'asc');
    else { setSortBy(field); setSortOrder('asc'); }
  };

  const { data: rawData = [] as any, isLoading } = useQuery({
    queryKey: ['collaborators', search, showInactive],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (!showInactive) p.set('activeOnly', 'true');
      if (search) p.set('search', search);
      const res = await apiGet<{ data: Collaborator[] }>(`/collaborators?${p}`);
      return (res as unknown as { data: Collaborator[] }).data ?? res;
    },
  });

  const { data: collaboratorDetail, isLoading: detailLoading } = useQuery({
    queryKey: ['collaborator-detail', selected?.id],
    queryFn: () => apiGet<Collaborator>(`/collaborators/${selected!.id}`),
    enabled: !!selected?.id,
  });

  const data = useMemo(() => sortCollaborators(rawData as Collaborator[], sortBy, sortOrder), [rawData, sortBy, sortOrder]);

  const createMutation = useMutation({
    mutationFn: (body: Partial<Collaborator>) => apiPost<Collaborator>('/collaborators', body),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['collaborators'] }); setShowNew(false); setForm({}); toast.success('Collaboratore creato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: Partial<Collaborator> & { id: string }) =>
      apiPatch<Collaborator>(`/collaborators/${id}`, body),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['collaborators'] });
      setSelected(updated);
      setEditMode(false);
      toast.success('Aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      apiPatch<Collaborator>(`/collaborators/${id}`, { active }),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['collaborators'] });
      if (selected?.id === updated.id) setSelected(updated);
      toast.success(updated.active ? 'Collaboratore riattivato' : 'Collaboratore disattivato');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/collaborators/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['collaborators'] });
      setDeleteTargetId(null);
      if (selected?.id === deleteTargetId) { setSelected(null); setEditMode(false); }
      toast.success('Collaboratore eliminato logicamente');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkDeactivateMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/collaborators/bulk/deactivate', { ids }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['collaborators'] });
      sel.clearSelection();
      setShowBulkDeactivateConfirm(false);
      toast.success(`${res.updated} collaboratori disattivati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkReactivateMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/collaborators/bulk/reactivate', { ids }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['collaborators'] });
      sel.clearSelection();
      setShowBulkReactivateConfirm(false);
      toast.success(`${res.updated} collaboratori riattivati`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: (ids: string[]) => apiPost<BulkResult>('/collaborators/bulk/delete', { ids }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['collaborators'] });
      sel.clearSelection();
      setShowBulkDeleteConfirm(false);
      toast.success(`${res.updated} collaboratori eliminati logicamente`);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleCreate = (e: React.FormEvent) => { e.preventDefault(); createMutation.mutate(form); };
  const handleUpdate = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selected) return;
    updateMutation.mutate({ id: selected.id, ...form });
  };

  const selectedIdsList = Array.from(sel.selectedIds);

  const collaboratorColumns: ColumnDef<Collaborator>[] = [
    {
      key: 'icon', label: '',
      render: (c) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }} onClick={e => e.stopPropagation()}>
          <Avatar src={(c as unknown as { avatarUrl?: string | null }).avatarUrl} name={c.name} size={28} />
        </div>
      ),
    },
    {
      key: 'name', label: 'Nome',
      render: (c) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 500, fontSize: 'var(--text-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.name}</div>
          <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{c._count?.events ?? 0} eventi</div>
        </div>
      ),
    },
    {
      key: 'contact', label: 'Contatto',
      render: (c) => (
        <div>
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
            <Mail size={11} /> {c.email || '—'}
          </p>
          {c.phone && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', display: 'flex', alignItems: 'center', gap: '0.25rem', marginTop: '0.125rem' }}>
            <Phone size={11} /> {c.phone}
          </p>}
        </div>
      ),
    },
    {
      key: 'role', label: 'Ruolo',
      render: (c) => <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{c.role}</span>,
    },
    {
      key: 'status', label: 'Stato',
      render: (c) => <Badge variant={c.active ? 'success' : 'muted'}>{c.active ? 'Attivo' : 'Inattivo'}</Badge>,
    },
    {
      key: 'actions', label: '',
      render: (c) => (
        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }} onClick={(e) => e.stopPropagation()}>
          <RowActionsMenu actions={[
            { label: 'Apri', icon: <ChevronRight size={14} />, onClick: () => setSelected(c) },
            { label: c.active ? 'Disattiva' : 'Riattiva', icon: c.active ? <UserX size={14} /> : <UserCheck size={14} />, onClick: () => toggleActiveMutation.mutate({ id: c.id, active: !c.active }) },
            { label: 'Elimina', icon: <Trash2 size={14} />, variant: 'danger', onClick: () => setDeleteTargetId(c.id) },
          ]} />
        </div>
      ),
    },
  ];

  return (
    <div className="page-enter" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <PageHeader
        title="Collaboratori"
        subtitle={`${data.length} collaboratori`}
        actions={[
          { label: 'Nuovo', icon: <Plus size={16} />, onClick: () => { setForm({}); setShowNew(true); }, variant: 'primary' },
        ]}
      />

      {/* Filters */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: '1 1 auto', maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-faint)' }} />
          <input
            placeholder="Cerca collaboratori…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', color: 'var(--color-text)', fontSize: 'var(--text-sm)', padding: '0.5rem 0.75rem 0.5rem 2.25rem', width: '100%', outline: 'none' }}
          />
        </div>
        <button
          onClick={() => setShowInactive(v => !v)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-sm)', color: showInactive ? 'var(--color-text)' : 'var(--color-text-muted)' }}
        >
          {showInactive ? <ToggleRight size={18} style={{ color: 'var(--color-primary)' }} /> : <ToggleLeft size={18} />}
          Mostra inattivi
        </button>
      </div>

      {/* Bulk action bar */}
      <BulkActionBar
        count={sel.selectedCount}
        onClear={sel.clearSelection}
        actions={[
          { label: 'Disattiva', icon: <UserX size={13} />, variant: 'secondary', onClick: () => setShowBulkDeactivateConfirm(true), loading: bulkDeactivateMutation.isPending },
          { label: 'Riattiva', icon: <UserCheck size={13} />, variant: 'secondary', onClick: () => setShowBulkReactivateConfirm(true), loading: bulkReactivateMutation.isPending },
          { label: 'Elimina', icon: <Trash2 size={13} />, variant: 'danger', onClick: () => setShowBulkDeleteConfirm(true), loading: bulkDeleteMutation.isPending },
        ]}
      />

      {isLoading ? (
        Array.from({ length: 4 }).map((_, i) => <SkeletonRow key={i} />)
      ) : (
      <DataTable
        columns={collaboratorColumns}
        data={data}
        gridColumns="68px 2fr 1.5fr 1fr 0.5fr 44px"
        rowKey={(c) => c.id}
        onRowClick={(c) => { setSelected(c); setEditMode(false); }}
        emptyState={
          <EmptyState icon={<Search size={32} />} title="Nessun collaboratore" action={<Button size="sm" onClick={() => setShowNew(true)}>Nuovo</Button>} />
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSort={(field) => handleSort(field as CollabSortField)}
        selectable
        selectedIds={sel.selectedIds}
        onSelectionChange={(ids) => sel.setSelectedIds(ids)}
        isLoading={isLoading}
        stickyHeader
      />
      )}

      {/* Detail Drawer */}
      <Drawer
        open={!!selected}
        onClose={() => { setSelected(null); setEditMode(false); }}
        title={selected?.name}
        footer={editMode ? (
          <>
            <Button variant="ghost" onClick={() => setEditMode(false)}>Annulla</Button>
            <Button onClick={handleUpdate} loading={updateMutation.isPending}>Salva</Button>
          </>
        ) : (
          <div style={{ display: 'flex', gap: '0.5rem', width: '100%', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => selected && toggleActiveMutation.mutate({ id: selected.id, active: !selected.active })}
              >
                {selected?.active ? 'Disattiva' : 'Riattiva'}
              </Button>
              <Button
                variant="danger"
                size="sm"
                onClick={() => selected && setDeleteTargetId(selected.id)}
              >
                Elimina
              </Button>
            </div>
            <Button onClick={() => { setForm(selected ?? {}); setEditMode(true); }}>Modifica</Button>
          </div>
        )}
      >
        {selected && (
          editMode ? (
            <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Input label="Nome *" value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
              <Input label="Email" type="email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
              <Input label="Telefono" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
              <Input label="Ruolo *" value={form.role ?? ''} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} required />
            </form>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              <div>
                <InfoRow label="Email" value={selected.email} />
                <InfoRow label="Telefono" value={selected.phone} />
                <InfoRow label="Ruolo" value={selected.role} />
                <InfoRow label="Note" value={selected.notes} />
              </div>

              {/* Progetti assegnati */}
              <div>
                <h3 style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text)', marginBottom: '0.75rem' }}>Progetti assegnati</h3>
                {detailLoading ? (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Caricamento…</div>
                ) : !collaboratorDetail?.projectAssignments || collaboratorDetail.projectAssignments.length === 0 ? (
                  <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>Nessun progetto assegnato</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                    {collaboratorDetail.projectAssignments.map((a: ProjectCollaboratorAssignment) => (
                      <div key={a.id} style={{
                        background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)',
                        padding: '0.75rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem', color: 'var(--color-text)'
                      }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem' }}>
                            <Link
                              to={`/projects/${a.project?.id}`}
                              style={{ fontSize: 'var(--text-sm)', fontWeight: 500, color: 'var(--color-text)', textDecoration: 'none' }}
                              onClick={e => e.stopPropagation()}
                            >
                              {a.project?.title || 'Progetto senza titolo'}
                            </Link>
                            <ExternalLink size={12} style={{ color: 'var(--color-text-faint)' }} />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                            <span>{COLLAB_ROLE_LABELS[a.role] || a.role}</span>
                            {a.completedAt && <span>·</span>}
                            {a.completedAt && <span>Completato {new Date(a.completedAt).toLocaleDateString('it-IT')}</span>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.375rem' }}>
                          <Badge
                            variant={a.paymentStatus === 'PAGATO' ? 'success' : a.paymentStatus === 'PARZIALE' ? 'warning' : 'muted'}
                          >
                            {a.paymentStatus === 'PAGATO' ? 'Pagato' : a.paymentStatus === 'PARZIALE' ? 'Parziale' : 'Non pagato'}
                          </Badge>
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>€ {parseFloat(String(a.feeExpected ?? 0)).toFixed(2)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )
        )}
      </Drawer>

      {/* New Modal */}
      <Modal open={showNew} onClose={() => setShowNew(false)} title="Nuovo collaboratore"
        footer={<><Button variant="ghost" onClick={() => setShowNew(false)}>Annulla</Button><Button onClick={handleCreate} loading={createMutation.isPending}>Crea</Button></>}
      >
        <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Nome *" value={form.name ?? ''} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          <Input label="Email" type="email" value={form.email ?? ''} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} />
          <Input label="Telefono" value={form.phone ?? ''} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
          <Input label="Ruolo *" value={form.role ?? ''} onChange={e => setForm(f => ({ ...f, role: e.target.value }))} required placeholder="es. Secondo fotografo" />
        </form>
      </Modal>

      {/* Single delete confirm */}
      <ConfirmDialog
        open={!!deleteTargetId}
        onClose={() => setDeleteTargetId(null)}
        onConfirm={() => deleteTargetId && deleteMutation.mutate(deleteTargetId)}
        loading={deleteMutation.isPending}
        title="Eliminare il collaboratore?"
        description="Il collaboratore verrà disattivato e contrassegnato come eliminato. Lo storico eventi e progetti rimarrà visibile."
        confirmLabel="Elimina"
      />

      {/* Bulk deactivate confirm */}
      <ConfirmDialog
        open={showBulkDeactivateConfirm}
        onClose={() => setShowBulkDeactivateConfirm(false)}
        onConfirm={() => bulkDeactivateMutation.mutate(selectedIdsList)}
        loading={bulkDeactivateMutation.isPending}
        title={`Disattivare ${sel.selectedCount} collaboratori?`}
        description="I collaboratori selezionati verranno impostati come inattivi."
        confirmLabel="Disattiva"
      />

      {/* Bulk reactivate confirm */}
      <ConfirmDialog
        open={showBulkReactivateConfirm}
        onClose={() => setShowBulkReactivateConfirm(false)}
        onConfirm={() => bulkReactivateMutation.mutate(selectedIdsList)}
        loading={bulkReactivateMutation.isPending}
        title={`Riattivare ${sel.selectedCount} collaboratori?`}
        description="I collaboratori selezionati verranno riattivati."
        confirmLabel="Riattiva"
      />

      {/* Bulk delete confirm */}
      <ConfirmDialog
        open={showBulkDeleteConfirm}
        onClose={() => setShowBulkDeleteConfirm(false)}
        onConfirm={() => bulkDeleteMutation.mutate(selectedIdsList)}
        loading={bulkDeleteMutation.isPending}
        requiresConfirmCheck
        title={`Eliminare ${sel.selectedCount} collaboratori?`}
        description="I collaboratori selezionati verranno disattivati ed eliminati logicamente. Lo storico eventi e pagamenti rimarrà."
        confirmLabel="Elimina"
      />
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: '0.5rem' }}>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', width: 80, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>{value}</span>
    </div>
  );
}
